import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
const STEP = [{
  n: '1',
  t: 'You type',
  d: 'The palette shows navigation and actions immediately from a local list, so the first paint never waits on the network. Every private page in the app is in that list.'
}, {
  n: '2',
  t: 'Scoped query',
  d: 'After a short debounce the query goes to the proxy, which derives your company from the verified token. Under impersonation that is the impersonated company, so a search narrows with you.'
}, {
  n: '3',
  t: 'Scoped services answer',
  d: 'Properties, units and residents come from endpoints that resolve the company server-side. The unscoped admin resolver is deliberately not used: it returns every company’s properties.'
}, {
  n: '4',
  t: 'Shaped and fenced',
  d: 'The proxy resolves one destination per row and asserts the company on every entity row before answering. A row from another company is dropped and counted, never rendered.'
}, {
  n: '5',
  t: 'Knowledge base',
  d: 'Articles are searched alongside records and filtered by audience, so an internal article never appears for someone who may not read it.'
}];
export function SearchTab() {
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      pt: '16px',
      maxWidth: 780
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 18,
      fontWeight: 600,
      mb: '4px'
    }
  }, "Universal search architecture"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '20px'
    }
  }, "One keystroke surface over navigation, records and the knowledge base. Cmd-K anywhere in the app, or the Search item in the sidebar. Admin only for now."), /*#__PURE__*/React.createElement(Stack, {
    spacing: "10px",
    sx: {
      mb: '26px'
    }
  }, STEP.map(s => /*#__PURE__*/React.createElement(Stack, {
    key: s.n,
    direction: "row",
    spacing: "12px",
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '13px'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 24,
      height: 24,
      flex: '0 0 auto',
      borderRadius: '7px',
      display: 'grid',
      placeItems: 'center',
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      fontSize: 12,
      fontWeight: 700
    }
  }, s.n), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5,
      fontWeight: 600
    }
  }, s.t), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      fontSize: 13
    }
  }, s.d))))), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600,
      mb: '6px'
    }
  }, "Why there is no search index"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '18px'
    }
  }, "There was one, and it was removed. A denormalised index only pays for itself once several services have to be fanned out on every keystroke, and until then it costs a reconciliation pass, a delete pass and a second tenant-isolation model to audit. It also failed quietly: a partition holding rows written before destinations existed answered every query, so the live path was never consulted and the palette said \u201Cno matches\u201D for records that were right there. Live scoped endpoints answer in roughly the same time and cannot go stale. If latency ever argues for an index again, it comes back with deletes, expiry and a schema version from the first commit."), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600,
      mb: '6px'
    }
  }, "What is searchable"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Properties, units, residents and the company directory, plus knowledge base articles and every page in the app. Accounts are not: the lookup is admin-only, crosses companies, and opens no page. Each result carries the destination the proxy resolved for it, so a result can never be a dead link \u2014 and anything it could not resolve is counted in the proxy log rather than silently disappearing."));
}